import { Capacitor } from "@capacitor/core";
import { Modal } from "antd";
import { svg } from "../Assets/svg";
import useAppUpdate from "../helpers/useAppUpdate";
import { FaceDetection } from "../plugins/faceDetection";

type AppUpdateModalProps = {
  /** True while any update flow (store / OTA bundle / APK) needs the modal. */
  isModalOpen: boolean;
  handleClose: () => void;
  /** Runs the OTA bundle (useCapacitorUpdater) or APK (useApkUpdater) update. */
  handlePress?: () => Promise<void>;
  updateInfo?: any;
  /** OTA bundle update available (CapacitorUpdater flow). */
  isInAppUpdate?: boolean;
  /** Native store (App Store / Play Store) update available. */
  isStoreUpdate?: boolean;
  /** Android in-app APK update available. */
  isApkUpdate?: boolean;
};

const AppUpdateModal = ({
  isModalOpen,
  handleClose,
  handlePress,
  updateInfo,
  isInAppUpdate,
  isStoreUpdate,
  isApkUpdate,
}: AppUpdateModalProps) => {
  const { openAppStore, performImmediateUpdate } = useAppUpdate();

  const getTitle = () => {
    if (isApkUpdate) return "App Update Available";
    if (isStoreUpdate) {
      return Capacitor.getPlatform() === "ios"
        ? "App Store Update Available"
        : "Google Play Update Available";
    }
    if (isInAppUpdate) return "App Update Available";
    return "Update Available";
  };

  const getDescription = () => {
    if (isApkUpdate) {
      const version = updateInfo?.availableVersionName;
      const buildNumber = updateInfo?.availableVersionCode;
      const details = [
        version ? `Version ${version}` : undefined,
        Number.isFinite(buildNumber) && buildNumber > 0
          ? `Build ${buildNumber}`
          : undefined,
      ]
        .filter(Boolean)
        .join(" · ");

      return details
        ? `A new update (${details}) is available. Update to install it.`
        : "A new update is available. Update to install it.";
    }
    if (isStoreUpdate) {
      return "A new version is available from your app store. Update to continue using the latest version.";
    }
    if (isInAppUpdate) {
      return "A new app update is ready to install. Please keep the app open while it updates.";
    }
    return "We've made some improvements. Please update your app to the latest version.";
  };

  /**
   * Single entry point for every update flow. Priority matches the prompts:
   * APK (Android in-app update) -> store update -> OTA bundle update.
   */
  const onUpdate = () => {
    FaceDetection.stopScan().catch(() => {});

    if (isApkUpdate) {
      handlePress?.().then();
    } else if (isStoreUpdate) {
      if (Capacitor.getPlatform() === "ios") {
        openAppStore().then();
      } else {
        performImmediateUpdate().then();
      }
    } else if (isInAppUpdate) {
      handlePress?.().then();
    }

    handleClose();
  };

  return (
    <Modal
      open={isModalOpen || isInAppUpdate}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"45rem"}
      className="commonModalClass"
      rootClassName="app-update-modal-root"
      centered
      zIndex={200000}
      onCancel={handleClose}
    >
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center">
        <div className="fw-bold fs-4">{getTitle()}</div>
        <div className="fw-normal fs-6">{getDescription()}</div>
        <div className="row justify-content-center mt-2">
          <div className="col-sm-auto col-6">
            <button
              className="btn btn-lg btn-outline-secondary px-4 text-uppercase"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
          <div className="col-sm-auto col-6">
            <button
              className="btn btn-lg btn-primary px-4 text-uppercase"
              onClick={onUpdate}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AppUpdateModal;
